import type { LocationEventInput } from "@ehes/contracts";

export type QueuedWebLocation = LocationEventInput & {
  tripId: string;
  attemptCount: number;
  nextAttemptAt: number;
};

const DATABASE_NAME = "ehes-driver-location";
const DATABASE_VERSION = 1;
const EVENT_STORE = "events";
const META_STORE = "metadata";
const TRIP_SEQUENCE_INDEX = "tripSequence";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Tarayıcı konum kuyruğu okunamadı."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Tarayıcı konum kuyruğu güncellenemedi."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Tarayıcı konum kuyruğu işlemi iptal edildi."));
  });
}

let databasePromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (!("indexedDB" in window)) {
    return Promise.reject(new Error("Bu tarayıcı dayanıklı konum kuyruğunu desteklemiyor."));
  }
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const eventStore = database.objectStoreNames.contains(EVENT_STORE)
        ? request.transaction!.objectStore(EVENT_STORE)
        : database.createObjectStore(EVENT_STORE, { keyPath: "eventId" });
      if (!eventStore.indexNames.contains(TRIP_SEQUENCE_INDEX)) {
        eventStore.createIndex(TRIP_SEQUENCE_INDEX, ["tripId", "sequenceNumber"], { unique: true });
      }
      if (!database.objectStoreNames.contains(META_STORE)) {
        database.createObjectStore(META_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error ?? new Error("Tarayıcı konum kuyruğu açılamadı."));
    };
    request.onblocked = () => {
      databasePromise = null;
      reject(new Error("Konum kuyruğu başka bir sekme tarafından kilitlendi."));
    };
  });
  return databasePromise;
}

function retryDelayMs(attemptNumber: number) {
  const normalizedAttempt = Math.max(1, Math.floor(attemptNumber));
  return Math.min(5_000 * 2 ** (normalizedAttempt - 1), 5 * 60_000);
}

export async function enqueueWebLocation(
  tripId: string,
  event: Omit<LocationEventInput, "eventId" | "sequenceNumber">,
): Promise<QueuedWebLocation> {
  const database = await openDatabase();
  const transaction = database.transaction([EVENT_STORE, META_STORE], "readwrite");
  const eventStore = transaction.objectStore(EVENT_STORE);
  const metaStore = transaction.objectStore(META_STORE);
  const sequenceKey = `sequence:${tripId}`;
  const current = await requestResult<{ key: string; value: number } | undefined>(metaStore.get(sequenceKey));
  // Web ve native istemci aynı seferde sırayla kullanılabilir. Web sayacını
  // epoch-milisaniye alanında tutmak native istemcinin küçük yerel sayacıyla
  // çakışmadan, tarayıcı içinde monoton sıra üretir.
  const sequenceNumber = Math.max(Date.now(), (current?.value ?? 0) + 1);
  const queuedEvent: QueuedWebLocation = {
    ...event,
    tripId,
    eventId: crypto.randomUUID(),
    sequenceNumber,
    attemptCount: 0,
    nextAttemptAt: 0,
  };
  metaStore.put({ key: sequenceKey, value: sequenceNumber });
  eventStore.add(queuedEvent);
  await transactionDone(transaction);
  return queuedEvent;
}

export async function getPendingWebLocationBatch(
  tripId: string,
  limit = 100,
): Promise<QueuedWebLocation[]> {
  const database = await openDatabase();
  const transaction = database.transaction(EVENT_STORE, "readonly");
  const index = transaction.objectStore(EVENT_STORE).index(TRIP_SEQUENCE_INDEX);
  const range = IDBKeyRange.bound([tripId, 0], [tripId, Number.MAX_SAFE_INTEGER]);
  const now = Date.now();
  const events = await new Promise<QueuedWebLocation[]>((resolve, reject) => {
    const result: QueuedWebLocation[] = [];
    const request = index.openCursor(range, "next");
    request.onerror = () => reject(request.error ?? new Error("Konum kuyruğu okunamadı."));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor || result.length >= limit) {
        resolve(result);
        return;
      }
      const event = cursor.value as QueuedWebLocation;
      if (event.nextAttemptAt > now) {
        resolve(result);
        return;
      }
      result.push(event);
      cursor.continue();
    };
  });
  await transactionDone(transaction);
  return events;
}

export async function acknowledgeWebLocationBatch(eventIds: string[]): Promise<void> {
  if (eventIds.length === 0) return;
  const database = await openDatabase();
  const transaction = database.transaction(EVENT_STORE, "readwrite");
  const store = transaction.objectStore(EVENT_STORE);
  for (const eventId of eventIds) store.delete(eventId);
  await transactionDone(transaction);
}

export async function deferWebLocationBatch(events: QueuedWebLocation[]): Promise<number> {
  if (events.length === 0) return 0;
  const database = await openDatabase();
  const transaction = database.transaction(EVENT_STORE, "readwrite");
  const store = transaction.objectStore(EVENT_STORE);
  let longestDelay = 0;
  for (const event of events) {
    const attemptCount = event.attemptCount + 1;
    const delay = retryDelayMs(attemptCount);
    longestDelay = Math.max(longestDelay, delay);
    store.put({
      ...event,
      attemptCount,
      nextAttemptAt: Date.now() + delay,
    } satisfies QueuedWebLocation);
  }
  await transactionDone(transaction);
  return longestDelay;
}

export async function getWebLocationQueueCount(tripId: string): Promise<number> {
  const database = await openDatabase();
  const transaction = database.transaction(EVENT_STORE, "readonly");
  const index = transaction.objectStore(EVENT_STORE).index(TRIP_SEQUENCE_INDEX);
  const range = IDBKeyRange.bound([tripId, 0], [tripId, Number.MAX_SAFE_INTEGER]);
  const count = await requestResult(index.count(range));
  await transactionDone(transaction);
  return count;
}
